import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Landmark, Users } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FloLogo from "@/components/FloLogo";
import RbiBadge from "@/components/RbiBadge";

const KYC_LABEL = {
  complete: { text: "KYC verified", cls: "border-[#1E8A5B]/30 text-[#1E8A5B] bg-[#E5F4EC]" },
  pending: { text: "KYC review pending", cls: "border-[#C77F1E]/30 text-[#8F5A16] bg-[#FBF1E1]" },
  not_started: { text: "KYC not started", cls: "border-border text-muted-foreground bg-muted" },
  error: { text: "KYC — action needed", cls: "border-destructive/30 text-destructive bg-destructive/10" },
};

const BUSINESS_TYPE_LABEL = {
  sole_proprietor: "Sole Proprietor",
  partnership: "Partnership",
  company: "Private Limited / OPC",
  limited_liability_partnership: "Limited Liability Partnership (LLP)",
  individual: "Individual",
};

export default function Account() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ phone: "", primary_product: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/account/profile");
      setData(data);
      setForm({
        phone: data.profile?.phone || "",
        primary_product: data.profile?.primary_product || "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load your profile.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data: updated } = await api.patch("/account/profile", form);
      setData((d) => ({ ...d, profile: { ...d.profile, ...updated.profile } }));
      await refresh();
      setEditing(false);
      toast.success("Contact details updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-muted-foreground">
        <FloLogo variant="lockup-light" size={44} />
        <div className="flex items-center text-sm">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading your profile…
        </div>
      </div>
    );
  }

  const p = data.profile || {};
  const addr = p.physical_address || {};
  const payout = data.payout || {};
  const counterparties = data.counterparties || [];
  const kyc = KYC_LABEL[data.kyc_status || "not_started"] || KYC_LABEL.not_started;

  return (
    <div className="min-h-screen" data-testid="account-root">
      <header className="border-b bg-white">
        <div className="max-w-4xl mx-auto flex items-center justify-between p-4 md:p-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              data-testid="account-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <FloLogo variant="lockup-light" size={30} />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-6 md:p-10 space-y-6">
        <RbiBadge />

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <p className="text-sm text-muted-foreground mb-1">My account</p>
            <h1 className="font-display text-3xl md:text-4xl font-extrabold text-foreground tracking-tight">
              {p.legal_name || data.email}
            </h1>
          </div>
          <span
            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border ${kyc.cls}`}
            data-testid="kyc-status-badge"
          >
            {kyc.text}
          </span>
        </div>

        {/* Company details */}
        <Card>
          <CardContent className="p-6 md:p-8 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display text-xl font-bold">Company details</h2>
              <span className="text-xs text-muted-foreground">Fixed at onboarding</span>
            </div>
            <Row label="Legal name" value={p.legal_name} />
            <Row label="Owner name" value={p.owner_name} />
            <Row label="Business type" value={BUSINESS_TYPE_LABEL[p.business_type] || p.business_type} />
            <Row label="GSTIN" value={p.tax_gst} mono />
            {p.business_id && (
              <Row label={p.business_type === "company" ? "CIN" : "LLP ID"} value={p.business_id} mono />
            )}
            {p.tax_id && <Row label="Business PAN" value={p.tax_id} mono />}
            <Row
              label="Address"
              value={
                [addr.address_line, addr.city, addr.state, addr.postal_code].filter(Boolean).join(", ") || "—"
              }
            />
          </CardContent>
        </Card>

        {/* Contact details (editable) */}
        <Card>
          <CardContent className="p-6 md:p-8 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">Contact details</h2>
              {!editing ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditing(true)}
                  data-testid="account-edit-btn"
                >
                  Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setForm({ phone: p.phone || "", primary_product: p.primary_product || "" });
                      setEditing(false);
                    }}
                    data-testid="account-cancel-btn"
                  >
                    Cancel
                  </Button>
                  <Button size="sm" onClick={save} disabled={saving} data-testid="account-save-btn">
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                    Save
                  </Button>
                </div>
              )}
            </div>

            <Row label="Email" value={data.email} />
            {!editing ? (
              <>
                <Row label="Phone" value={p.phone} mono />
                <Row label="Primary export product" value={p.primary_product} />
              </>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
                    Phone
                  </Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="+91 98470 12345"
                    className="h-11 sf-mono"
                    data-testid="account-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
                    Primary export product
                  </Label>
                  <Input
                    value={form.primary_product}
                    onChange={(e) => setForm((f) => ({ ...f, primary_product: e.target.value }))}
                    placeholder="Spices / Tea / Coffee / Other"
                    className="h-11"
                    data-testid="account-product-input"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Payout bank */}
        <Card>
          <CardContent className="p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-primary/10 grid place-items-center shrink-0">
                <Landmark className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="font-display text-xl font-bold">Payout bank account</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Where your rupees land after each USD, GBP, or EUR deposit is converted.
                </p>
                <div className="mt-4 space-y-2">
                  <Row label="Bank" value={payout.bank_name} />
                  <Row label="Holder" value={payout.holder_name} />
                  <Row label="Account" value={payout.number_masked} mono />
                  <Row label="IFSC" value={payout.ifsc} mono />
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-6">
              Need to change your payout account?{" "}
              <Link to="/contact" className="text-primary font-semibold hover:underline">
                Contact support
              </Link>
              .
            </p>
          </CardContent>
        </Card>

        {/* Counterparties (overseas buyers) */}
        <Card>
          <CardContent className="p-6 md:p-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="h-12 w-12 rounded-xl bg-primary/10 grid place-items-center shrink-0">
                <Users className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="font-display text-xl font-bold">Overseas buyers</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  From your transaction history — {counterparties.length}{" "}
                  {counterparties.length === 1 ? "buyer" : "buyers"}.
                </p>
              </div>
            </div>
            {counterparties.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No transactions yet.</p>
            ) : (
              <div className="divide-y" data-testid="counterparties-list">
                {counterparties.map((c, i) => (
                  <div
                    key={`${c.name}-${c.country}-${i}`}
                    className="flex items-center justify-between py-3"
                    data-testid="counterparty-row"
                  >
                    <span className="font-medium text-foreground">{c.name}</span>
                    <span className="text-sm text-muted-foreground">{c.country_name || c.country || "—"}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-2 gap-1">
      <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</span>
      <span className={`font-medium text-foreground text-right break-all ${mono ? "sf-mono" : ""}`}>
        {value || "—"}
      </span>
    </div>
  );
}
